/**
 * ConsultationsPage — J7-W3 task 3b/3c (PRD §12.3.10)
 *
 * Lists every persona consultation in the active org. Columns: type
 * (single/council), personas, question (truncated), reference entity,
 * created_at. Filter by type. Click a row → detail page.
 *
 * "+ New Consultation" CTA opens PersonaConsultDialog with a single/council
 * mode toggle (3c reuses 3b's surface — no duplicated component).
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessagesSquare, Plus } from 'lucide-react';
import { t } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { useRorosConsultations } from '@/hooks/roros/actors';
import { useRorosActors } from '@/hooks/roros/actors';
import { formatDate } from '@/lib/localeFormatters';
import { PersonaConsultDialog } from './PersonaConsultDialog';
import type { RorosPersonaConsultationDTO, RorosActorDTO } from '@/types/roros/actors';

function typeBadge(type: string): React.ReactNode {
  if (type === 'council') {
    return <Badge className="bg-blue-100 text-blue-700">{t('Council')}</Badge>;
  }
  return <Badge variant="secondary">{t('Single')}</Badge>;
}

function truncate(s: string, n = 80): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1).trim()}…` : s;
}

export function ConsultationsPage() {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [isFormOpen, setIsFormOpen] = useState(false);

  const params = useMemo(() => {
    if (typeFilter === 'all') return undefined;
    return { type: typeFilter };
  }, [typeFilter]);

  const consultationsQuery = useRorosConsultations(params);
  const actorsQuery = useRorosActors();

  const actorMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of (actorsQuery.data as RorosActorDTO[] | undefined) ?? []) {
      m.set(a.id, a.name);
    }
    return m;
  }, [actorsQuery.data]);

  return (
    <RorosPage
      title={t('Consultations')}
      icon={<MessagesSquare className="h-5 w-5" aria-hidden />}
      actions={
        <Button
          onClick={() => setIsFormOpen(true)}
          leftIcon={<Plus className="h-4 w-4" />}
          data-testid="consultations-new-button"
        >
          {t('New Consultation')}
        </Button>
      }
    >
      <div className="mt-4 flex flex-wrap gap-3">
        <Select
          label={t('Type')}
          value={typeFilter}
          onValueChange={setTypeFilter}
          options={[
            { value: 'all', label: t('All types') },
            { value: 'single', label: t('Single persona') },
            { value: 'council', label: t('Council (multi-persona)') },
          ]}
        />
      </div>

      <div className="mt-6">
        <QueryPage
          query={consultationsQuery}
          emptyMessage={t('No consultations yet. Open a Knowledge Persona to get advice.')}
          emptyIcon={<MessagesSquare className="h-8 w-8" />}
          emptyAction={
            <Button onClick={() => setIsFormOpen(true)} leftIcon={<Plus className="h-4 w-4" />}>
              {t('Run your first consultation')}
            </Button>
          }
        >
          {(consultations) => {
            const list = consultations as RorosPersonaConsultationDTO[];
            if (list.length === 0) {
              return (
                <div className="text-center text-sm text-muted-foreground py-8">
                  {t('No consultations match the current filters.')}
                </div>
              );
            }
            return (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table
                  className="w-full min-w-[640px] text-sm"
                  aria-label={t('Consultations')}
                  data-testid="consultations-table"
                >
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-24">{t('Type')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">{t('Personas')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5">{t('Question')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-28">{t('Reference')}</th>
                      <th className="text-left text-xs font-semibold text-muted-foreground px-4 py-2.5 w-28">{t('Created')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((c) => (
                      <tr
                        key={c.id}
                        className="border-b border-border last:border-0 hover:bg-muted/20 cursor-pointer transition-colors"
                        onClick={() => navigate(`/roros/consultations/${c.id}`)}
                        data-testid="consultation-row"
                      >
                        <td className="px-4 py-3">{typeBadge(c.consultationType)}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {(c.personaIds ?? []).map((id) => actorMap.get(id) ?? id.slice(0, 8)).join(', ')}
                        </td>
                        <td className="px-4 py-3 text-foreground">{truncate(c.question, 100)}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {c.referenceEntityType ? `${c.referenceEntityType}:${(c.referenceEntityId ?? '').slice(0, 8)}` : t('—')}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {c.createdAt ? formatDate(new Date(c.createdAt)) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }}
        </QueryPage>
      </div>

      <PersonaConsultDialog
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onCreated={(c) => {
          consultationsQuery.refetch();
          // Drop the user straight into the resulting consultation so they
          // can read the persona response without a list refresh round-trip.
          if (c?.id) navigate(`/roros/consultations/${c.id}`);
        }}
      />
    </RorosPage>
  );
}
